import { api, ProjectDetail, TaskStatus, Epic, TaskItem } from '../api';
import { STATUSES } from '../lib/status';
import { escapeHtml } from '../lib/utils';
import { flattenTree } from '../lib/taskTree';
import { openModal } from './modal';
import { toast } from './toast';

export interface TaskFormOptions {
  initialStatus?: TaskStatus | null;
  epicId?: string | null;
  parentId?: string | null;
  sprintId?: string | null;
  epics: Epic[];
  tasks: TaskItem[];
  onChanged: () => void;
}

export function openTaskForm(
  project: ProjectDetail,
  options: TaskFormOptions
): void {
  const status = options.initialStatus || 'not_started';
  const epicId = options.epicId || '';

  const epicOptions = [
    '<option value="">No epic</option>',
    ...options.epics.map(
      (e) => `<option value="${escapeHtml(e.id)}" ${e.id === epicId ? 'selected' : ''}>${escapeHtml(e.name)}</option>`
    ),
  ].join('');

  // Parent options: any existing task except the one we're creating (new task has no id).
  const parentOptions = [
    '<option value="">No parent (top-level task)</option>',
    ...flattenTree(options.tasks)
      .map(({ task, depth }) => {
        const label = `${'· '.repeat(depth)}${task.title}`;
        return `<option value="${escapeHtml(task.id)}" ${
          task.id === options.parentId ? 'selected' : ''
        }>${escapeHtml(label)}</option>`;
      })
      .join(''),
  ].join('');

  const assigneeOptions = [
    '<option value="">Unassigned</option>',
    ...project.members.map(
      (m) => `<option value="${escapeHtml(m.id)}">${escapeHtml(m.name)}</option>`
    ),
  ].join('');

  openModal(
    `
    <h2 style="font-size:20px;margin-bottom:16px;">New Task</h2>
    <form id="new-task-form">
      <div class="field">
        <label>Title</label>
        <input class="input" name="title" placeholder="What needs to be done?" />
      </div>
      <div style="display:grid;grid-template-columns:1fr 1fr;gap:16px;">
        <div class="field">
          <label>Status</label>
          <select class="select" name="status">
            ${STATUSES.map(
              (s) => `<option value="${s.key}" ${s.key === status ? 'selected' : ''}>${s.label}</option>`
            ).join('')}
          </select>
        </div>
        <div class="field">
          <label>Story points</label>
          <input class="input" type="number" min="0" name="points" value="0" />
        </div>
      </div>
      <div style="display:grid;grid-template-columns:1fr 1fr;gap:16px;">
        <div class="field">
          <label>Epic</label>
          <select class="select" name="epic">${epicOptions}</select>
        </div>
        <div class="field">
          <label>Assignee</label>
          <select class="select" name="assignee">${assigneeOptions}</select>
        </div>
      </div>
      <div class="field">
        <label>Parent task (subtask of…)</label>
        <select class="select" name="parent">${parentOptions}</select>
      </div>
      <div class="field">
        <label>Description (Markdown supported)</label>
        <textarea class="textarea" name="description" rows="6" placeholder="Describe the task in detail. You can use **bold**, lists, code blocks…"></textarea>
      </div>
      <div class="form-error hidden" id="task-error"></div>
      <div style="display:flex;gap:10px;justify-content:flex-end;">
        <button type="button" class="btn btn-ghost" data-close>Cancel</button>
        <button type="submit" class="btn btn-primary">Create task</button>
      </div>
    </form>
  `,
    (body, close) => {
      const form = body.querySelector('#new-task-form') as HTMLFormElement;
      const errorEl = body.querySelector('#task-error') as HTMLElement;
      body.querySelector('[data-close]')!.addEventListener('click', close);
      body.querySelector<HTMLInputElement>('input[name="title"]')?.focus();

      form.addEventListener('submit', async (e) => {
        e.preventDefault();
        errorEl.classList.add('hidden');
        const data = new FormData(form);
        const title = String(data.get('title') || '').trim();
        if (!title) {
          errorEl.textContent = 'Task title is required';
          errorEl.classList.remove('hidden');
          return;
        }
        try {
          await api.post(`/projects/${project.id}/tasks`, {
            title,
            description: String(data.get('description') || ''),
            status: String(data.get('status') || 'not_started'),
            storyPoints: Number(data.get('points')) || 0,
            assigneeId: String(data.get('assignee') || '') || null,
            epicId: String(data.get('epic') || '') || null,
            parentId: String(data.get('parent') || '') || null,
            sprintId: options.sprintId || null,
          });
          toast('Task created');
          close();
          options.onChanged();
        } catch (e: any) {
          errorEl.textContent = e.message || 'Could not create task';
          errorEl.classList.remove('hidden');
        }
      });
    }
  );
}
